import React, { useState } from 'react'
import FormErrorMessage from './FormErrorMessage'

interface FormInputProps {
  label?: string
  name: string
  type?: string
  value: string
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  placeholder?: string
  placeholderTextStyleClassName?: string
  showPasswordToggle?: boolean
  children?: React.ReactNode
  buttonText?: string
  onButtonClick?: () => void
  buttonIsActive?: boolean
  error?: string
  subtitle?: string
  marginTop?: number
  marginBottom?: number
  maxLength?: number
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void
  onFocus?: (e: React.FocusEvent<HTMLInputElement>) => void
  /** 붙여넣는 순간 바로 처리해야 하는 입력칸용 (링크 가져오기 등) */
  onPaste?: (e: React.ClipboardEvent<HTMLInputElement>) => void
  readOnly?: boolean
  addClassName?: string
  /** 모바일 등에서 키보드 종류 힌트 (예: numeric → 숫자 키패드) */
  inputMode?: React.HTMLAttributes<HTMLInputElement>['inputMode']
}

const FormInput: React.FC<FormInputProps> = ({
  label,
  name,
  type = 'text',
  value,
  onChange,
  placeholder = '',
  placeholderTextStyleClassName,
  showPasswordToggle = false,
  children,
  buttonText,
  onButtonClick,
  buttonIsActive = true,
  error,
  subtitle,
  marginTop = 0,
  marginBottom = 16,
  maxLength,
  onBlur,
  onFocus,
  onPaste,
  readOnly = false,
  addClassName = '',
  inputMode,
}) => {
  const [showPassword, setShowPassword] = useState(false)

  const inputType = showPasswordToggle && showPassword ? 'text' : type

  return (
    <div
      className={`${addClassName}`}
      style={{ marginTop: `${marginTop}px`, marginBottom: `${marginBottom}px` }}
    >
      {label && (
        <label
          htmlFor={name}
          className={`block font-pretendard font-bold text-16px leading-20px text-brown-80%-main ${
            subtitle ? 'mb-8px' : 'mb-10px'
          }`}
        >
          {label}
        </label>
      )}
      {/* 안내문은 제목에 붙이고 입력칸과는 띄운다 — 셋이 같은 간격이면 한 덩어리로 뭉쳐 보인다 */}
      {subtitle && (
        <div className="text-12px text-textColorText2 mb-12px">{subtitle}</div>
      )}
      <div className="flex gap-4px items-start">
        <div
          className={`relative flex-1 ${
            showPasswordToggle ? 'has-toggle' : ''
          }`}
        >
          <input
            type={inputType}
            id={name}
            name={name}
            value={value}
            onChange={onChange}
            onBlur={onBlur}
            onFocus={onFocus}
        onPaste={onPaste}
            readOnly={readOnly}
            className={`w-full py-10px px-12px border rounded-8px bg-transparent focus:outline-none 
              font-pretendard font-regular text-16px text-textColorText2 leading-24px
              ${
                error
                  ? 'border-semanticError focus:border-semanticError'
                  : 'border-divider-divider-1 focus:border-brandPrimary'
              } 
            ${showPasswordToggle ? 'pr-48px' : ''} 
            ${
              placeholderTextStyleClassName ?? 'placeholder:text-textColorText3'
            }
            ${readOnly ? 'cursor-not-allowed' : ''}`}
            placeholder={placeholder}
            maxLength={maxLength}
            inputMode={inputMode}
          />
          {showPasswordToggle && (
            <button
              type="button"
              className="absolute right-12px top-1/2 -translate-y-1/2 bg-none border-none cursor-pointer p-4px flex items-center justify-center"
              onClick={() => setShowPassword(!showPassword)}
              aria-label={showPassword ? '비밀번호 숨기기' : '비밀번호 표시'}
            >
              {showPassword ? (
                <img
                  src="/images/eye-icon.png"
                  alt="eye"
                  className="w-24px h-24px object-contain"
                />
              ) : (
                <img
                  src="/images/eye-disabled-icon.png"
                  alt="eye-disabled"
                  className="w-24px h-24px object-contain"
                />
              )}
            </button>
          )}
        </div>
        {buttonText && (
          <button
            type="button"
            className={`
              py-11px px-13px rounded-8px cursor-pointer whitespace-nowrap text-white min-w-[80px]
              ${buttonIsActive ? 'bg-4B3621-80%' : 'bg-brandPrimary-10%'}
            `}
            onClick={onButtonClick}
          >
            <span
              className={`font-pretendard font-700 text-16px leading-20px text-center ${
                buttonIsActive ? 'text-textColorText1W' : 'text-brown-80%-main'
              }`}
            >
              {buttonText}
            </span>
          </button>
        )}
      </div>
      <FormErrorMessage error={error} />
      {children && <div className="mt-4px">{children}</div>}
    </div>
  )
}

export default FormInput
